import React from 'react';
import { motion } from 'framer-motion';
import { 
    ShieldCheck, Lock, Eye, Key, FileCheck, 
    ShieldAlert, Server, Globe, CheckCircle2,
    Cloud, Fingerprint, Database, ArrowRight
} from 'lucide-react';
import PublicLayout from '../components/layout/PublicLayout';

const SecurityPage = () => {
    const pillars = [
        {
            icon: Lock,
            title: "Data Encryption",
            description: "All data is encrypted at rest using AES-256 and in transit via TLS 1.3, ensuring your data remains private and secure.",
            color: "blue"
        },
        {
            icon: Fingerprint,
            title: "Access Control",
            description: "Granular role-based access control (RBAC) and mandatory multi-factor authentication for all administrative actions.",
            color: "indigo"
        },
        {
            icon: FileCheck,
            title: "Compliance",
            description: "Built to exceed global privacy standards including GDPR, FERPA, and SOC 2 Type II requirements.",
            color: "emerald"
        },
        {
            icon: ShieldCheck,
            title: "Automated Audits",
            description: "Continuous security monitoring and automated vulnerability scanning ensure our infrastructure is always hardened.",
            color: "violet"
        }
    ];

    return (
        <PublicLayout>
            <div className="pt-40 pb-20 px-6">
                <div className="max-w-7xl mx-auto">
                    <motion.div 
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="text-center mb-32"
                    >
                        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-500/10 border border-blue-500/20 text-blue-500 dark:text-blue-400 text-xs font-black uppercase tracking-[0.2em] mb-8">
                            <ShieldCheck className="w-4 h-4" /> Trusted by 500+ Institutions
                        </div>
                        <h1 className="text-5xl md:text-8xl font-black tracking-tighter mb-8 bg-gradient-to-b from-zinc-950 to-zinc-600 dark:from-white dark:to-white/40 bg-clip-text text-transparent">
                            Bank-Grade Security. <br /> Built for Education.
                        </h1>
                        <p className="text-neutral-600 dark:text-neutral-400 text-xl md:text-2xl max-w-3xl mx-auto font-medium leading-relaxed">
                            We understand that institutional data is your most valuable asset. Our mission is to keep it safe, private, and always available.
                        </p>
                    </motion.div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-40">
                        {pillars.map((pillar, i) => (
                            <motion.div
                                key={i}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ delay: i * 0.1 }}
                                className="group p-12 rounded-[3.5rem] bg-white/50 dark:bg-zinc-900/50 border border-neutral-200 dark:border-white/5 hover:border-blue-500/30 transition-all duration-500 relative overflow-hidden shadow-sm dark:shadow-none"
                            >
                                <div className="absolute top-0 right-0 p-12 opacity-5 translate-x-1/2 -translate-y-1/2 group-hover:scale-110 transition-transform duration-1000">
                                    <pillar.icon className="w-[300px] h-[300px]" />
                                </div>
                                <div className={`w-20 h-20 rounded-3xl bg-${pillar.color}-500/10 flex items-center justify-center mb-10 border border-${pillar.color}-500/20`}>
                                    <pillar.icon className={`w-10 h-10 text-${pillar.color}-500 dark:text-${pillar.color}-400`} />
                                </div>
                                <h3 className="text-3xl font-black text-zinc-900 dark:text-white mb-6 tracking-tight">{pillar.title}</h3>
                                <p className="text-neutral-600 dark:text-zinc-500 text-lg font-medium leading-relaxed max-w-md">
                                    {pillar.description}
                                </p>
                            </motion.div>
                        ))}
                    </div>

                    {/* Infrastructure Section */}
                    <div className="mb-40">
                        <div className="flex flex-col lg:flex-row items-center gap-32">
                            <div className="flex-1">
                                <h2 className="text-4xl md:text-6xl font-black tracking-tighter text-zinc-900 dark:text-white mb-8">
                                    Resilient Cloud <br /> Infrastructure.
                                </h2>
                                <p className="text-neutral-600 dark:text-zinc-500 text-lg md:text-xl font-medium leading-relaxed mb-12">
                                    Our platform runs on globally distributed, Tier-4 data centers with multiple redundancy layers and failover protocols.
                                </p>
                                <div className="space-y-8">
                                    {[
                                        { title: "Distributed Architecture", desc: "No single point of failure with multi-region replication." },
                                        { title: "Real-time Monitoring", desc: "24/7/365 security operations center (SOC) coverage." },
                                        { title: "Encrypted Backups", desc: "Daily snapshots stored in isolated, air-gapped environments." }
                                    ].map((item, i) => (
                                        <div key={i} className="flex gap-6">
                                            <div className="w-12 h-12 rounded-2xl bg-neutral-100 dark:bg-zinc-900 border border-neutral-200 dark:border-white/5 flex items-center justify-center shrink-0">
                                                <div className="w-2 h-2 rounded-full bg-blue-500" />
                                            </div>
                                            <div>
                                                <div className="text-zinc-900 dark:text-white font-black uppercase text-xs tracking-widest mb-2">{item.title}</div>
                                                <div className="text-neutral-500 dark:text-zinc-500 font-medium leading-relaxed">{item.desc}</div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                            <div className="flex-1 w-full">
                                <div className="aspect-square relative flex items-center justify-center p-12">
                                    <div className="absolute inset-0 border-[40px] border-neutral-200/50 dark:border-zinc-900/50 rounded-full animate-[spin_20s_linear_infinite]"></div>
                                    <div className="absolute inset-20 border-[1px] border-neutral-200 dark:border-white/5 rounded-full"></div>
                                    <div className="absolute inset-40 border-[1px] border-neutral-200/50 dark:border-white/10 rounded-full animate-[spin_10s_linear_infinite_reverse]"></div>
                                    <div className="relative z-10 w-40 h-40 rounded-[3rem] bg-gradient-to-br from-blue-600 to-indigo-600 flex items-center justify-center shadow-[0_0_100px_rgba(37,99,235,0.4)]">
                                        <ShieldCheck className="w-20 h-20 text-white" />
                                    </div>
                                    
                                    {/* Orbital Icons */}
                                    {[Lock, Eye, Key, Globe].map((Icon, i) => (
                                        <div 
                                            key={i} 
                                            className="absolute w-16 h-16 rounded-2xl bg-white dark:bg-zinc-900 border border-neutral-200 dark:border-white/10 flex items-center justify-center text-neutral-400 dark:text-zinc-500 shadow-2xl transition-all hover:text-zinc-900 dark:hover:text-white hover:border-neutral-300 dark:hover:border-white/20 cursor-pointer"
                                            style={{
                                                transform: `rotate(${i * 90}deg) translate(240px) rotate(-${i * 90}deg)`
                                            }}
                                        >
                                            <Icon className="w-6 h-6" />
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>


                </div>
            </div>
        </PublicLayout>
    );
};

export default SecurityPage;
